import { useActor } from "@xstate/react";
import Link from "next/link";
import { type FC, useMemo } from "react";

import type { DocumentPageMetadata } from "@moment/api-collab/api-types";

import { createPageAuthorQuery } from "~/state/machines/pages/page-author.query";
import { Avatar, AvatarFallback, AvatarImage } from "~/ui/Avatar";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "~/ui/HoverCard";

import { useEasel } from "../../easel";
import { Row } from "../../layout/row/Row";

export const Byline: FC<{
	activePageMetadata: DocumentPageMetadata | null;
}> = ({ activePageMetadata }) => {
	const { workspaceName, documentId, pageId } = useEasel();

	const machine = useMemo(
		() =>
			createPageAuthorQuery(
				workspaceName,
				documentId,
				pageId,
				activePageMetadata?.authorID ?? ""
			),
		[workspaceName, documentId, pageId, activePageMetadata?.authorID]
	);

	const [{ context: query }] = useActor(machine);

	if (!query.data) {
		return <div className="rich-text my-4" />;
	}

	const profileImageUrl = query.data?.profileImageUrl;
	const profileImageFallback = query.data?.profileImageFallback ?? "?";
	const displayName = query.data?.displayName ?? "--";
	const githubUsername = query.data?.githubUsername;
	const bio = query.data?.bio;

	return (
		<div className="rich-text mt-4 mb-8">
			<Row>
				<div className="flex items-center gap-2">
					<Avatar size="sm">
						<AvatarImage src={profileImageUrl} />
						<AvatarFallback>{profileImageFallback}</AvatarFallback>
					</Avatar>
					<div className="text-secondary text-base">
						By{" "}
						{query.data ? (
							<HoverCard openDelay={100}>
								<HoverCardTrigger asChild>
									<span className="text-primary font-medium hover:underline">
										{displayName}
									</span>
								</HoverCardTrigger>
								<HoverCardContent>
									<div className="flex justify-between space-x-4 grow">
										<Avatar size="lg">
											<AvatarImage src={profileImageUrl} />
											<AvatarFallback>{profileImageFallback}</AvatarFallback>
										</Avatar>

										<div className="grow space-y-1 text-sm">
											<div className="text-base font-semibold">
												{displayName}{" "}
												{githubUsername && (
													<Link
														className="text-sm font-light text-secondary hover:text-secondary"
														target="_blank"
														rel="noopener noreferrer"
														href={`https://github.com/${githubUsername}`}
													>
														@{githubUsername}
													</Link>
												)}
											</div>

											{bio && <div className="text-secondary">{bio}</div>}
										</div>
									</div>
								</HoverCardContent>
							</HoverCard>
						) : (
							<span className="text-primary">{displayName}</span>
						)}
					</div>
				</div>
			</Row>
		</div>
	);
};
